import { FC, useState } from "react";

import {
  useAcknowledgementNeed,
  useGenerateCode,
  useValidateCode,
} from '@repo/edtech-api';
import {
  Button,
  Checkbox,
} from "@repo/edtech-ui-kit";

import { formatPhone } from '../../../lib';
import { ConfirmCodeModal } from "../confirm-code-modal";

import st from "./AcknowledgementBlock.module.scss";

type Props = {
  unit: string,
  material: string,
  publication: string,
  label?: string,
  buttonText?: string,
  action?: string,
  codeLength?: number,
  successTitle?: string,
  successSubtitle?: string,
  onSuccess?: () => void,
};

const AGREEMENT_TYPE = "31SOk0uUOrf1wfH5yKGO56ofekQ";

const ACTION_TARGET = "";

export const AcknowledgementBlock: FC<Props> = ({
  unit,
  material,
  publication,
  label = "Я ознакомился(ась) с материалами",
  buttonText = "Подписать инструктаж",
  action = "sps-submition",
  codeLength = 6,
  successTitle,
  successSubtitle,
  onSuccess,
}) => {
  const [ isChecked, setIsChecked ] = useState(false);
  const [ isModalOpen, setIsModalOpen ] = useState(false);
  const [ isSuccess, setIsSuccess ] = useState(false);
  const [ errorMessage, setErrorMessage ] = useState("");
  const [ generateResult, setGenerateResult ] = useState<{ ttl: number, phone: string } | null>(null);

  const {
    data: ackData,
    isLoading: isAckLoading,
    mutate,
  } = useAcknowledgementNeed({ unit, material });
  const { trigger: generateCode, isMutating: isGenerating } = useGenerateCode();
  const { trigger: validateCode, isMutating: isValidating } = useValidateCode();

  if ((isAckLoading || !ackData?.need) && !isModalOpen) {
    return null;
  }

  const agreementInfo = {
    unit,
    publication,
    material,
  };

  const handleButtonClick = async () => {
    if (!isChecked) return;
    try {
      const result = await generateCode({ action, agreementInfo });
      setGenerateResult({ ttl: result.ttl, phone: formatPhone(result.phone) });
      setIsModalOpen(true);
    } catch (error) {
      console.error("Failed to generate code:", error);
    }
  };

  const handleConfirm = async (code: string) => {
    setErrorMessage("");
    try {
      const result = await validateCode({
        action,
        code,
        agreementInfo: {
          ...agreementInfo,
          type: AGREEMENT_TYPE,
          actionTarget: ACTION_TARGET,
          agreementText: label,
        },
      });

      if (result.isValid) {
        setIsSuccess(true);
        mutate();
      } else {
        setErrorMessage(result.errorMessage || "Неверный код");
      }
    } catch (error) {
      console.error("Failed to validate code:", error);
      setErrorMessage("Произошла ошибка. Попробуйте ещё раз");
    }
  };

  const handleResendCode = async (): Promise<number | void> => {
    try {
      const result = await generateCode({ action, agreementInfo });
      setGenerateResult({ ttl: result.ttl, phone: formatPhone(result.phone) });
      setErrorMessage("");
      return result.ttl;
    } catch (error) {
      console.error("Failed to resend code:", error);
    }
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setIsSuccess(false);
    setErrorMessage("");
    setGenerateResult(null);
    onSuccess?.();
  };

  return (
    <div className={st.block}>
      <Checkbox
        checked={isChecked}
        classNames={st.checkbox}
        iconClassNames={st.checkbox_icon}
        label={label}
        onChange={() => setIsChecked((prev) => !prev)}
      />
      <Button
        className={st.button}
        disabled={!isChecked || isGenerating}
        onClick={handleButtonClick}
      >
        {buttonText}
      </Button>

      <ConfirmCodeModal
        codeLength={codeLength}
        errorMessage={errorMessage}
        isLoading={isValidating}
        isOpen={isModalOpen}
        isSuccess={isSuccess}
        phoneNumber={generateResult?.phone}
        resendTimeout={generateResult?.ttl ?? 0}
        successSubtitle={successSubtitle}
        successTitle={successTitle}
        onClose={handleClose}
        onConfirm={handleConfirm}
        onResendCode={handleResendCode}
      />
    </div>
  );
};
